import { ChangeDetectionStrategy, Component, inject, output, signal } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { AuthenticationService } from '../../core/authentication/authentication.service';
import { UserService } from '../../core/services/user.service';
import { SignedInUser } from '../../shared/types/user';
import { ButtonComponent, ButtonType } from '../../shared/components/button/button.component';
import { generateErrorId } from '../../shared/utils/unique-id-utils';
import { ClientLoggingService } from '../../core/services/client-logging.service';
import { AppUrls } from '../../routing/app-urls';

@Component({
    selector: 'juyo-sign-in',
    styleUrl: 'sign-in.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [ReactiveFormsModule, ButtonComponent],
    template: `
        <div class="heading-section">
            <h1 class="heading">Welcome back</h1>
            <p class="subtitle">Access your space to connect the dots.</p>
        </div>

        <div class="error-banner">
            @if (showInvalidUsername()) {
                Oops! The email / username you entered is not valid.
            }
            @if (showFailedAttempt()) {
                Oops! The email or password you entered is incorrect. Please try again.
            }
            @if (showInvalidMfaCode()) {
                Oops! The authentication code you entered is incorrect. Please try again.
            }
            @if (showUserEndpointError()) {
                Oops! We couldn't retrieve your user information. Please try refreshing the page or contact support if
                the issue persists.
            }
        </div>

        <form class="form" [formGroup]="form" (keydown)="onKeyDown($event)" novalidate>
            <label class="field-label" for="username">USERNAME</label>
            <input
                type="text"
                name="username"
                id="username"
                class="field"
                placeholder="james@organization.com"
                formControlName="username"
            />
            @if (!showPasswordInput()) {
                <juyo-button
                    [title]="'NEXT'"
                    [isLoading]="verifyDomainInProgress()"
                    [useFullWidth]="true"
                    [buttonType]="ButtonType.PURPLE"
                    (clicked)="onCheckIdp()"
                >
                    Next
                </juyo-button>
            }
            @if (showPasswordInput() && !showMfaInput() && !showMfaSelection()) {
                <label class="field-label" for="password">PASSWORD</label>
                <input
                    type="password"
                    name="password"
                    id="password"
                    class="field"
                    placeholder="Enter your password"
                    formControlName="password"
                />
                <juyo-button
                    [title]="'SIGN IN'"
                    [isLoading]="signInInProgress()"
                    [useFullWidth]="true"
                    [buttonType]="ButtonType.PURPLE"
                    (clicked)="onSignIn()"
                >
                    Sign In
                </juyo-button>
            }
            @if (showMfaSelection()) {
                <label class="field-label">CHOOSE A VERIFICATION METHOD</label>
                @if (allowedMfaTypes().includes('TOTP')) {
                    <juyo-button
                        [useFullWidth]="true"
                        [buttonType]="ButtonType.PURPLE"
                        [disabled]="mfaSelectionInProgress()"
                        (clicked)="onSelectMfa('TOTP')"
                    >
                        Authenticator app
                    </juyo-button>
                }
                @if (allowedMfaTypes().includes('EMAIL')) {
                    <juyo-button
                        [useFullWidth]="true"
                        [buttonType]="ButtonType.PURPLE"
                        [disabled]="mfaSelectionInProgress()"
                        (clicked)="onSelectMfa('EMAIL')"
                    >
                        Email code
                    </juyo-button>
                }
            }
            @if (showMfaInput()) {
                <label class="field-label" for="mfaCode">{{
                    mfaMethod() === 'EMAIL' ? 'EMAIL CODE' : 'AUTHENTICATION CODE'
                }}</label>
                <input
                    type="text"
                    name="mfaCode"
                    id="mfaCode"
                    class="field"
                    placeholder="000000"
                    maxlength="6"
                    formControlName="mfaCode"
                    autocomplete="one-time-code"
                />
                @if (mfaMethod() === 'EMAIL') {
                    <p class="mfa-hint">We've sent a 6-digit code to your email.</p>
                }
                <juyo-button
                    [title]="'CONFIRM'"
                    [isLoading]="mfaConfirmInProgress()"
                    [useFullWidth]="true"
                    [buttonType]="ButtonType.PURPLE"
                    (clicked)="onConfirmMfa()"
                >
                    Confirm
                </juyo-button>
                @if (mfaMethod() === 'TOTP') {
                    <div class="support-hint">
                        <a href="mailto:support@juyoanalytics.com">Lost access to your device? Contact support.</a>
                    </div>
                }
            }

            <div class="forgot-password">
                @if (showMfaInput() && allowedMfaTypes().length > 1) {
                    <button type="button" class="link-button" (click)="onTryAnotherWay()">
                        Try another way instead
                    </button>
                } @else if (!showMfaInput() && !showMfaSelection()) {
                    <a [href]="resetPasswordUrl">Forgot your password?</a>
                }
            </div>
        </form>
    `,
})
export class SignInComponent {
    public form: FormGroup<SignInForm>;
    public showInvalidUsername = signal(false);
    public showFailedAttempt = signal(false);
    public showInvalidMfaCode = signal(false);
    public showUserEndpointError = signal(false);
    public signInInProgress = signal(false);
    public verifyDomainInProgress = signal(false);
    public mfaConfirmInProgress = signal(false);
    public mfaSelectionInProgress = signal(false);
    public showPasswordInput = signal(false);
    public showMfaInput = signal(false);
    public showMfaSelection = signal(false);
    public mfaMethod = signal<'TOTP' | 'EMAIL' | null>(null);
    public allowedMfaTypes = signal<string[]>([]);

    public signedIn = output<void>();

    private authenticationService = inject(AuthenticationService);
    private clientLoggingService = inject(ClientLoggingService);
    private userService = inject(UserService);

    protected readonly ButtonType = ButtonType;

    public resetPasswordUrl = '/' + AppUrls.resetPasswordUrl;

    constructor() {
        this.form = new FormGroup<SignInForm>({
            username: new FormControl('', [Validators.required, Validators.maxLength(50)]),
            password: new FormControl('', [Validators.required, Validators.maxLength(50)]),
            mfaCode: new FormControl('', [Validators.required, Validators.minLength(6), Validators.maxLength(6)]),
        });
    }

    public onCheckIdp(): void {
        this.verifyDomainInProgress.set(true);
        const username = this.form.value.username;
        if (username) {
            this.authenticationService.getUserIdpInfo(username).subscribe((userIdpInfo) => {
                if (userIdpInfo && userIdpInfo.idp && userIdpInfo.valid) {
                    this.showInvalidUsername.set(false);
                    if (userIdpInfo.idp === 'Cognito') {
                        this.verifyDomainInProgress.set(false);
                        this.showPasswordInput.set(true);
                    } else {
                        this.authenticationService.federatedSignIn(userIdpInfo.idp);
                    }
                } else {
                    this.showInvalidUsername.set(true);
                    this.verifyDomainInProgress.set(false);
                }
            });
        }
    }

    public async onSignIn(): Promise<void> {
        try {
            this.signInInProgress.set(true);
            const formModel = this.form.value;
            const signInResult = await this.authenticationService.signIn(
                formModel.username as string,
                formModel.password as string,
            );
            this.handleSignInNextStep(signInResult);
        } catch (err) {
            this.handleError(err, SignInErrorType.Login);
        }
    }

    public async onSelectMfa(mfaType: 'TOTP' | 'EMAIL'): Promise<void> {
        try {
            this.mfaSelectionInProgress.set(true);
            this.showInvalidMfaCode.set(false);
            const result = await this.authenticationService.selectMfaType(mfaType);
            this.handleSignInNextStep(result);
        } catch (err) {
            this.mfaSelectionInProgress.set(false);
            this.handleError(err, SignInErrorType.Login);
        }
    }

    public async onConfirmMfa(): Promise<void> {
        try {
            this.mfaConfirmInProgress.set(true);
            this.showInvalidMfaCode.set(false);
            const code = this.form.value.mfaCode as string;
            const success = await this.authenticationService.confirmMfaCode(code);
            if (success) {
                this.loadUser();
            } else {
                this.mfaConfirmInProgress.set(false);
                this.showInvalidMfaCode.set(true);
            }
        } catch (err) {
            this.mfaConfirmInProgress.set(false);
            this.showInvalidMfaCode.set(true);
        }
    }

    private handleSignInNextStep(result: {
        isSignedIn: boolean;
        nextStep?: { signInStep?: string; allowedMFATypes?: string[] };
    }): void {
        if (result.isSignedIn) {
            this.loadUser();
            return;
        }
        const step = result.nextStep?.signInStep;
        if (step === 'CONFIRM_SIGN_IN_WITH_TOTP_CODE') {
            this.promptForCode('TOTP');
        } else if (step === 'CONFIRM_SIGN_IN_WITH_EMAIL_CODE') {
            this.promptForCode('EMAIL');
        } else if (step === 'CONTINUE_SIGN_IN_WITH_MFA_SELECTION') {
            this.allowedMfaTypes.set(result.nextStep?.allowedMFATypes ?? []);
            this.showMfaSelection.set(true);
            this.signInInProgress.set(false);
            this.mfaSelectionInProgress.set(false);
        } else {
            this.handleError('Unable to log in at Cognito', SignInErrorType.Login);
        }
    }

    private promptForCode(method: 'TOTP' | 'EMAIL'): void {
        this.mfaMethod.set(method);
        this.showMfaSelection.set(false);
        this.showMfaInput.set(true);
        this.signInInProgress.set(false);
        this.mfaSelectionInProgress.set(false);
        this.form.controls.mfaCode.reset();
    }

    public onTryAnotherWay(): void {
        this.showInvalidMfaCode.set(false);
        this.form.controls.mfaCode.reset();
        // Show the selection screen straight away so the password field doesn't flash, and
        // keep its buttons disabled until the fresh sign-in challenge arrives.
        this.showMfaInput.set(false);
        this.showMfaSelection.set(true);
        this.mfaSelectionInProgress.set(true);
        // Cognito has already committed to the chosen factor, so restart the sign-in to get
        // back to the MFA selection challenge.
        void this.onSignIn();
    }

    public onKeyDown(event: KeyboardEvent): void {
        if (event.key !== 'Enter') {
            this.showFailedAttempt.set(false);
            this.showInvalidMfaCode.set(false);
        }
    }

    private loadUser(): void {
        this.userService.loadUser(true).subscribe({
            next: (user: SignedInUser | null) => {
                if (user && user.id) {
                    this.showUserEndpointError.set(false);
                    this.signedIn.emit();
                } else {
                    this.handleError('Something went wrong while loading the user!', SignInErrorType.UserEndpoint);
                }
            },
            error: (error: Error) => {
                this.handleError(error, SignInErrorType.UserEndpoint);
            },
        });
    }

    private handleError(error: unknown, errorType: SignInErrorType): void {
        const correlationId = generateErrorId();
        this.clientLoggingService.log(correlationId, error);
        if (errorType === SignInErrorType.Login) {
            this.showFailedAttempt.set(true);
        } else if (errorType === SignInErrorType.UserEndpoint) {
            this.showUserEndpointError.set(true);
        }
        this.signInInProgress.set(false);
        this.mfaSelectionInProgress.set(false);
    }
}

interface SignInForm {
    username: FormControl<string | null>;
    password: FormControl<string | null>;
    mfaCode: FormControl<string | null>;
}

enum SignInErrorType {
    Login,
    UserEndpoint,
}
